import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, HeartHandshake, UserCheck, FileCheck2, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Steps from "../components/Steps";
import api from "../api";
import { SERVICES, CITIES } from "../mock";

const EMPLOYMENT = ["Full-time", "Part-time", "Occasional", "Live-in", "One-time"];

export default function PostJobPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const prefill = state || (() => { try { return JSON.parse(sessionStorage.getItem("ss_prefill") || "{}"); } catch (e) { return {}; } })();
  const [service, setService] = useState(prefill?.service || "nanny");
  const [title, setTitle] = useState("");
  const [employment, setEmployment] = useState("Part-time");
  const [citySlug, setCitySlug] = useState(prefill?.citySlug || "");
  const [location, setLocation] = useState(prefill?.location || "");
  const [startDate, setStartDate] = useState("");
  const [wage, setWage] = useState("");
  const [description, setDescription] = useState(prefill?.details || "");
  const [loading, setLoading] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const draftIdRef = useRef(prefill?.draftId || null);
  const saveTimer = useRef(null);

  const buildDetails = () => {
    const parts = [];
    if (title.trim()) parts.push(title.trim());
    const meta = [];
    if (employment) meta.push(`Employment: ${employment}`);
    if (startDate.trim()) meta.push(`Start: ${startDate.trim()}`);
    if (wage.trim()) meta.push(`Wage: ${wage.trim()}`);
    if (meta.length) parts.push(meta.join(" \u00b7 "));
    if (description.trim()) parts.push(description.trim());
    return parts.join("\n") || null;
  };

  // Auto-save the draft as the family types, so an abandoned post is still visible to admins.
  useEffect(() => {
    if (!location.trim()) return;
    if (!(title.trim() || description.trim() || wage.trim() || startDate.trim())) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/jobs/draft", {
          draft_id: draftIdRef.current, service, city_slug: citySlug || null,
          location: location.trim(), details: buildDetails(),
        });
        draftIdRef.current = data.id;
        setSavedDraft(true);
      } catch (e) { /* ignore */ }
    }, 1200);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, title, employment, citySlug, location, startDate, wage, description]);

  const submit = async (e) => {
    e.preventDefault();
    if (!location.trim()) return toast.error("Please enter a location.");
    setLoading(true);
    try {
      const { data } = await api.post("/jobs/draft", {
        draft_id: draftIdRef.current, service, city_slug: citySlug || null,
        location: location.trim(), details: buildDetails(),
      });
      navigate(`/checkout/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create job.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Header />
      <section className="hero-gradient">
        <div className="max-w-6xl mx-auto px-5 pt-12 pb-6 text-center">
          <h1 className="font-display text-4xl sm:text-5xl text-[#1f1a1b]">Find the right care</h1>
          <p className="text-[17px] text-[#5a5254] mt-3">Posted in minutes &mdash; vetted candidates apply to you.</p>
          <div className="mt-8"><Steps active={1} /></div>
        </div>
      </section>

      <section className="bg-[#fdfaf7]">
        <div className="max-w-6xl mx-auto px-5 py-12 grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
          {/* Form */}
          <form onSubmit={submit} className="rounded-3xl border border-black/[0.07] bg-white p-7 sm:p-9">
            <h2 className="font-display text-2xl text-[#1f1a1b]">Describe your role</h2>
            <p className="text-[15px] text-[#5a5254] mt-1">The more specific, the better the applications match.</p>

            <div className="mt-6 space-y-5">
              <Field label="Title">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Part-time nanny for 2 kids" className={inp} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={service} onChange={(e) => setService(e.target.value)} className={inp}>
                    {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Employment">
                  <select value={employment} onChange={(e) => setEmployment(e.target.value)} className={inp}>
                    {EMPLOYMENT.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="City">
                  <select value={citySlug} onChange={(e) => { setCitySlug(e.target.value); const c = CITIES.find((x) => x.slug === e.target.value); if (c) setLocation(c.name); }} className={inp}>
                    <option value="">Select a city…</option>
                    {CITIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}, {c.state}</option>)}
                  </select>
                </Field>
                <Field label="Location">
                  <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Brooklyn" className={inp} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Start date">
                  <input value={startDate} onChange={(e) => setStartDate(e.target.value)} placeholder="e.g. from Aug 1 / ASAP" className={inp} />
                </Field>
                <Field label={<>Wage / budget <span className="text-[#8a8285] font-normal">(optional)</span></>}>
                  <input value={wage} onChange={(e) => setWage(e.target.value)} placeholder="e.g. $28/hr, negotiable" className={inp} />
                </Field>
              </div>
              <Field label="Description">
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Schedule, number of children, specific needs…" className={`${inp} resize-none py-2.5`} />
              </Field>
            </div>

            <button type="submit" disabled={loading} className="w-full mt-7 h-13 py-4 inline-flex items-center justify-center gap-2 rounded-xl bg-[#7c2233] hover:bg-[#661b29] disabled:opacity-70 text-white font-medium transition-colors">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Continue <ArrowRight className="w-4 h-4" />
            </button>
            {savedDraft && (
              <p className="mt-2 text-center text-[13px] text-green-700" data-testid="draft-saved-note">Draft saved — you can finish and activate it later from your dashboard.</p>
            )}
          </form>

          {/* What happens next */}
          <aside className="rounded-3xl border border-black/[0.07] bg-white p-7 sm:p-9">
            <h2 className="font-display text-2xl text-[#1f1a1b]">What happens next?</h2>
            <p className="text-[15px] text-[#5a5254] mt-1">Trust and safety come first with us.</p>
            <ul className="mt-6 space-y-5">
              {[[ShieldCheck, "Reviewed by our team", "We check every listing \u2014 for maximum family safety."],
                [HeartHandshake, "100% no obligation", "No placement commission. You only pay the transparent membership."],
                [UserCheck, "Vetted candidates", "Applications only from verified, personally interviewed caregivers."],
                [FileCheck2, "References & background check", "Identity and references are checked before approval."]].map(([Icon, t, d]) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-[#f7e4e6] flex items-center justify-center"><Icon className="w-5 h-5 text-[#7c2233]" strokeWidth={1.7} /></div>
                  <div><p className="font-semibold text-[#1f1a1b]">{t}</p><p className="text-[14px] text-[#5a5254] mt-0.5">{d}</p></div>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-black/[0.06] flex items-start gap-2 text-[14px] text-[#5a5254]">
              <Sparkles className="w-4 h-4 text-[#7c2233] mt-0.5 shrink-0" />
              <span><strong className="text-[#1f1a1b]">1 month free</strong>, then $15.99/month &mdash; cancel anytime.</span>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const inp = "w-full h-11 rounded-xl border border-black/10 px-3 text-[15px] bg-white outline-none focus:border-[#7c2233] focus:ring-2 focus:ring-[#7c2233]/15 transition";

function Field({ label, children }) {
  return (<div><label className="text-[13px] font-medium text-[#3a3436] block mb-1.5">{label}</label>{children}</div>);
}
